import React, { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import {
  Boxes,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  KeyRound,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  UserRound,
} from 'lucide-react';
import { CmsContext } from '../../context/definitions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../shared/ui';
import { pageBack, pageTitle } from '../../shared/cmsPage/definitions';
import { iconFor, railMenu, resolveLabel, sortSubtenants } from '../../sidebar/definitions';
import { useSelectedItem } from '../../sidebar/useSelectedItem';
import { breadcrumbTrail, changePasswordItem, initialsFor, roleLabel } from '../definitions';

/** The server's own route: it ends the session and redirects to the login screen. */
const SIGN_OUT = '/logout';

/**
 * The bar across the top of the page column: the rail's collapse button, where the admin is
 * as breadcrumbs, and who they are as an account menu.
 *
 * It is rendered by the frame (`router/components/AppFrame.js`), outside the page column's
 * scroll, so it stays put while the page scrolls. It navigates the way the rail does, through
 * the frame's `onNavigate`, so a crumb and a rail item that go to the same screen behave the
 * same (a list opens on page one, the page opens at its top). A crumb that stands for the
 * page's own back link goes back through that link's handler instead (`breadcrumbTrail`).
 *
 * The account menu holds what is the admin's rather than the CMS's: the chapter and
 * subtenant they are working in, Change Password and Sign Out. The pickers were under the
 * logo in the rail. Choosing either sets a cookie and reloads, as it always has
 * (`shared/api/tenantApi.js`), because the tenant decides what the config holds.
 *
 * Geometry and colour are in `css/simple-sidebar.css`, by class name, as they are for the
 * rail. The menus are the shared Radix dropdown.
 */
/** A screen in a crumb's menu: its icon, its name, and a tick on the one the admin is in. */
const ScreenItem = ({ item, label, selected, onPick, onPreload }) => {
  const Icon = iconFor(item);
  return (
    <DropdownMenuItem
      aria-current={item === selected ? 'page' : undefined}
      onSelect={() => onPick(item)}
      onPointerEnter={() => onPreload?.(item)}
      onFocus={() => onPreload?.(item)}
    >
      <Icon size={16} aria-hidden="true" />
      <span className="topbar-menu-text">{label(item.text)}</span>
      {item === selected && <Check size={16} aria-hidden="true" />}
    </DropdownMenuItem>
  );
};

/** A crumb that opens a menu: its name, a chevron, and the look of where it stands. */
const MenuCrumb = ({ crumb, children }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger
      className={crumb.current ? 'crumb crumb-current' : 'crumb'}
      aria-current={crumb.current ? 'page' : undefined}
      title={crumb.label}
    >
      <span className="crumb-text">{crumb.label}</span>
      <ChevronDown size={14} aria-hidden="true" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="topbar-menu">
      {children}
    </DropdownMenuContent>
  </DropdownMenu>
);

const Crumb = ({ crumb, label, selected, onNavigate, onPreload }) => {
  // The tenant is the trail's anchor: a chip with the building in a tile of its own.
  const tenant = crumb.kind === 'tenant';
  const className = tenant ? 'crumb crumb-tenant' : 'crumb';
  const icon = tenant ? (
    <span className="crumb-tile" aria-hidden="true">
      <Building2 size={14} />
    </span>
  ) : null;
  if (crumb.sections) {
    // The top tier: every section, each opening its screens beside it.
    return (
      <MenuCrumb crumb={crumb}>
        {crumb.sections.map((section) => (
          <DropdownMenuSub key={section.label}>
            <DropdownMenuSubTrigger aria-current={section.current ? 'true' : undefined}>
              <span className="topbar-menu-text">{section.label}</span>
              {section.current && <Check size={16} aria-hidden="true" />}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="topbar-menu topbar-switch-menu">
              {section.items.map((item) => (
                <ScreenItem
                  key={item.text}
                  item={item}
                  label={label}
                  selected={selected}
                  onPick={onNavigate}
                  onPreload={onPreload}
                />
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </MenuCrumb>
    );
  }
  if (crumb.items) {
    /* The second tier: the screens beside this one in its section. Choosing this one goes
       back to it the way the crumb would have: through the page's own way back when it has
       one, so a list opens where the admin left it; and not at all when already there. */
    const pick = (item) => {
      if (item !== crumb.target) onNavigate(item);
      else if (crumb.onClick) crumb.onClick();
      else if (!crumb.current) onNavigate(item);
    };
    return (
      <MenuCrumb crumb={crumb}>
        {crumb.items.map((item) => (
          <ScreenItem
            key={item.text}
            item={item}
            label={label}
            selected={selected}
            onPick={pick}
            onPreload={onPreload}
          />
        ))}
      </MenuCrumb>
    );
  }
  if (crumb.current) {
    return (
      <span className="crumb crumb-current" aria-current="page" title={crumb.label}>
        {icon}
        <span className="crumb-text">{crumb.label}</span>
      </span>
    );
  }
  // The page's own way back (a record above this one, or the list it came from).
  if (crumb.onClick) {
    return (
      <button type="button" className="crumb" title={crumb.label} onClick={crumb.onClick}>
        {icon}
        <span className="crumb-text">{crumb.label}</span>
      </button>
    );
  }
  // The tenant with nowhere to go, on a menu that has no screen to call home; or a section
  // that is the only one, with nothing beside it to open.
  if (!crumb.target) {
    return (
      <span className={`${className} crumb-static`} title={crumb.label}>
        {icon}
        <span className="crumb-text">{crumb.label}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      className={className}
      title={crumb.label}
      onClick={() => onNavigate(crumb.target)}
      onPointerEnter={() => onPreload?.(crumb.target)}
      onFocus={() => onPreload?.(crumb.target)}
    >
      {icon}
      <span className="crumb-text">{crumb.label}</span>
    </button>
  );
};

/**
 * A picker in the account menu: a row naming what is current, opening a list to choose from.
 * The current choice is ticked, and choosing it again does nothing rather than reloading.
 */
const Switcher = ({ icon: Icon, caption, value, options, getKey, getLabel, isCurrent, onPick }) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger className="topbar-switch">
      <Icon size={16} aria-hidden="true" />
      <span className="topbar-switch-text">
        <span className="topbar-switch-caption">{caption}</span>
        <span className="topbar-switch-value">{value}</span>
      </span>
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent className="topbar-menu topbar-switch-menu">
      {options.map((option) => {
        const current = isCurrent(option);
        return (
          <DropdownMenuItem
            key={getKey(option)}
            aria-current={current ? 'true' : undefined}
            onSelect={() => {
              if (!current) onPick(option);
            }}
          >
            <span className="topbar-menu-text">{getLabel(option)}</span>
            {current && <Check size={16} aria-hidden="true" />}
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);

/**
 * The account menu: the admin's initials in a circle and their email, which is the only name
 * an admin account carries.
 */
const AccountMenu = ({
  user,
  menu,
  messages,
  tenants,
  selectedTenant,
  selectedSubtenant,
  showTenants,
  showSubtenants,
  onSelectTenant,
  onSelectSubtenant,
  onNavigate,
}) => {
  const email = user?.email || '';
  const initials = initialsFor(email);
  const role = roleLabel(user?.role);
  const changePassword = changePasswordItem(menu);
  const signOut = useCallback(() => {
    window.location.href = SIGN_OUT;
  }, []);

  const allTenants = tenants || [];
  const subtenants = selectedTenant?.subtenants || {};
  // The frame's answer when it has one (`loadTenantState`), worked out here only when it has
  // none, as the rail's picker did: a tenant object reaching a component by another route
  // once made this come out false on a tenant with three subtenants.
  const canPickTenant = showTenants ?? allTenants.length > 1;
  const canPickSubtenant = showSubtenants ?? Object.keys(subtenants).length > 1;
  const orderedTenants = useMemo(
    () => [...allTenants].sort((a, b) => (a.label || '').localeCompare(b.label || '')),
    [allTenants]
  );

  const avatar = (
    <span className="topbar-avatar" aria-hidden="true">
      {initials || <UserRound size={16} />}
    </span>
  );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className="topbar-user"
        // The email is hidden on a narrow window, so the button names itself.
        aria-label={email ? `Account: ${email}` : 'Account'}
      >
        {avatar}
        {email && <span className="topbar-user-name">{email}</span>}
        <ChevronDown className="topbar-user-chevron" size={14} aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="topbar-menu topbar-account-menu">
        {(email || role) && (
          <>
            <div className="topbar-menu-head">
              {avatar}
              <span className="topbar-menu-who">
                {email && <span className="topbar-menu-email">{email}</span>}
                {role && <span className="topbar-menu-role">{role}</span>}
              </span>
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        {(canPickTenant || canPickSubtenant) && (
          <>
            {canPickTenant && (
              <Switcher
                icon={Building2}
                caption={resolveLabel(messages, 'cms.tenantSelectorLabel', 'Chapter')}
                value={selectedTenant?.label || ''}
                options={orderedTenants}
                getKey={(tenant) => tenant.tenantId}
                getLabel={(tenant) => tenant.label}
                isCurrent={(tenant) => tenant.tenantId === selectedTenant?.tenantId}
                onPick={onSelectTenant}
              />
            )}
            {canPickSubtenant && (
              <Switcher
                icon={Boxes}
                caption={resolveLabel(messages, 'cms.subtenantSelectorLabel', 'Subtenant')}
                value={selectedSubtenant?.name || ''}
                options={sortSubtenants(subtenants)}
                getKey={(subtenant) => subtenant.id}
                getLabel={(subtenant) => subtenant.name}
                isCurrent={(subtenant) => subtenant.id === selectedSubtenant?.id}
                onPick={onSelectSubtenant}
              />
            )}
            <DropdownMenuSeparator />
          </>
        )}
        {changePassword && (
          <DropdownMenuItem onSelect={() => onNavigate(changePassword)}>
            <KeyRound size={16} aria-hidden="true" />
            <span className="topbar-menu-text">Change Password</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={signOut}>
          <LogOut size={16} aria-hidden="true" />
          <span className="topbar-menu-text">Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const AppTopBar = ({
  route,
  onNavigate,
  onPreload,
  // The page column has scrolled away from its top, so the bar lifts with a shadow.
  scrolled = false,
  railCollapsed = false,
  onToggleRail,
  onSelectTenant,
  onSelectSubtenant,
  showTenants,
  showSubtenants,
}) => {
  const { cmsConfig, selectedTenant, selectedSubtenant } = useContext(CmsContext);
  const serverMenu = cmsConfig?.sideMenu;
  // The rail's menu, without the account items the account menu holds.
  const menu = useMemo(() => railMenu(serverMenu), [serverMenu]);
  const messages = cmsConfig?.messages;
  const label = useCallback((text) => resolveLabel(messages, text), [messages]);
  const selected = useSelectedItem(menu, route);
  // The page's own title and way back, which the page publishes (`shared/cmsPage/definitions.js`).
  const title = useSyncExternalStore(pageTitle.subscribe, pageTitle.current, pageTitle.current);
  const back = useSyncExternalStore(pageBack.subscribe, pageBack.current, pageBack.current);

  // The subtenant is named where there is a choice of them, and not when it is the default:
  // "Default" on every screen says nothing.
  const subtenant =
    showSubtenants && selectedSubtenant && !selectedSubtenant.isDefault
      ? selectedSubtenant.name
      : '';
  const crumbs = useMemo(
    () =>
      breadcrumbTrail({
        menu,
        messages,
        route,
        selected,
        tenant: selectedTenant?.label,
        subtenant,
        pageTitle: title,
        back,
      }),
    [menu, messages, route, selected, selectedTenant, subtenant, title, back]
  );
  const toggleLabel = railCollapsed ? 'Expand menu' : 'Collapse menu';

  return (
    <header className="app-topbar" data-scrolled={scrolled ? '' : undefined}>
      <button
        type="button"
        className="rail-toggle"
        onClick={onToggleRail}
        aria-expanded={!railCollapsed}
        aria-controls="sidebar-wrapper"
        aria-label={toggleLabel}
        title={toggleLabel}
      >
        {railCollapsed ? (
          <PanelLeftOpen size={18} aria-hidden="true" />
        ) : (
          <PanelLeftClose size={18} aria-hidden="true" />
        )}
      </button>
      <span className="topbar-divider" aria-hidden="true" />
      <nav className="app-breadcrumbs" aria-label="Breadcrumb">
        <ol>
          {crumbs.map((crumb, index) => (
            <li key={crumb.kind}>
              {index > 0 && <ChevronRight className="crumb-sep" size={14} aria-hidden="true" />}
              <Crumb
                crumb={crumb}
                label={label}
                selected={selected}
                onNavigate={onNavigate}
                onPreload={onPreload}
              />
            </li>
          ))}
        </ol>
      </nav>
      <AccountMenu
        user={cmsConfig?.loggedInUser}
        menu={serverMenu}
        messages={messages}
        tenants={cmsConfig?.accessibleTenants}
        selectedTenant={selectedTenant}
        selectedSubtenant={selectedSubtenant}
        showTenants={showTenants}
        showSubtenants={showSubtenants}
        onSelectTenant={onSelectTenant}
        onSelectSubtenant={onSelectSubtenant}
        onNavigate={onNavigate}
      />
    </header>
  );
};

export default AppTopBar;
